import { OrderStatus } from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Space,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useNavigate, useParams } from 'react-router-dom';
import { AddressSelect } from '../components/OrderCheckoutAddressSelect';
import { OrderCheckoutPaymentSelect } from '../components/OrderCheckoutPaymentSelect';
import { OrderCheckoutShippingSelect } from '../components/OrderCheckoutShippingSelect';
import { OrderSummary } from '../components/OrderCheckoutSummary';
import {
  clearShippingAndPaymentMethodOnCheckoutOrderDocument,
  confirmCheckoutOrderDocument,
  setAddressOnCheckoutOrderDocument,
  setPaymentMethodOnCheckoutOrderDocument,
  setShippingMethodOnCheckoutOrderDocument,
} from '../graphql/mutations';
import { checkoutOrderDocument } from '../graphql/queries';
import { useState } from 'react';
import {
  IconCircle,
  IconCircleFilled,
  IconShoppingBag,
  IconTruckDelivery,
} from '@tabler/icons-react';

export const OrderCheckout = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const theme = useMantineTheme();
  const [isPersonalPickup, setIsPersonalPickup] = useState<boolean | undefined>();

  const { data, error, loading } = useQuery(checkoutOrderDocument, {
    variables: { orderId: orderId ?? '' },
    skip: !orderId,
    onCompleted: async ({ checkoutOrder }) => {
      if (!checkoutOrder) return;

      if (checkoutOrder.paymentMethodId === 'cm4bow5qp0009n82su9fyiisa') setIsPersonalPickup(true);
      else if (checkoutOrder.paymentMethodId) setIsPersonalPickup(false);

      if (checkoutOrder.address) return;

      if (checkoutOrder.customer.addresses?.length && checkoutOrder.customer.addresses.length > 0) {
        await setAddressOnActiveOrder({
          variables: {
            orderId: checkoutOrder.id,
            addressId: checkoutOrder.customer.addresses[0].id,
          },
        });
      }
    },
    onError: () => navigate('/checkout/complete'),
  });

  const [confirmCheckoutOrder, { loading: confirming }] = useMutation(
    confirmCheckoutOrderDocument,
    {
      onCompleted: ({ confirmCheckoutOrder: { id, package: packageNum, total } }) => {
        showNotification({
          id,
          color: 'teal',
          title: 'Objednávka potvrdená',
          message: `Objednávka č. ${packageNum} bola úspešne potvrdená. Celková suma objednávky je ${total} €.`,
        });

        navigate('/checkout/complete', { replace: true });
      },
      refetchQueries: [],
    }
  );

  const [setAddressOnActiveOrder] = useMutation(setAddressOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  const [setShippingMethod] = useMutation(setShippingMethodOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  const [setPaymentMethod] = useMutation(setPaymentMethodOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  const [clearShippingAndPaymentMethodOnCheckoutOrder] = useMutation(
    clearShippingAndPaymentMethodOnCheckoutOrderDocument,
    {
      refetchQueries: [checkoutOrderDocument],
    }
  );

  if (loading) return <Loader />;

  if (error || data?.checkoutOrder.status !== OrderStatus.Created)
    return (
      <Container mt="md">
        <Alert color="teal" title="Objednávka už bola spracovaná">
          Túto objednávku ste už spracovali. Ak potrebujete v objednávke niečo zmeniť, kontaktujte
          nás.
        </Alert>
      </Container>
    );

  if (!data?.checkoutOrder)
    return (
      <Container mt="md">
        <Alert color="red" title="Chyba">
          Zdá sa, že nastal problém s načítaním vašej objednávky. Prosím, kontaktujte nás pre pomoc.
        </Alert>
      </Container>
    );

  const { checkoutOrder: order } = data;

  return (
    <Container my="md" component={Stack}>
      <Group justify="space-between">
        <Group>
          <Avatar src={order.customer.avatar} name={order.customer.name} color="initials" />
          <Text fw={500}>{order.customer.name}</Text>
        </Group>
        <Text fw={500}>Objednávka č. {order.package}</Text>
      </Group>

      <Alert color="orange" title="Kontrola objednávky">
        Skontrolujte si objednané produkty nižšie. Ak by niečo nesedelo, alebo ak by ste chceli
        náhodou niečo omylom zakúpené zrušiť, nedokončujte objednávku a zavolajte nám. (0911 499
        771)
      </Alert>

      <OrderSummary order={order} />

      <Card p="md" withBorder component={Stack}>
        <Text fw={500}>Poslať kuriérom alebo osobný odber?</Text>
        <Card
          p="md"
          withBorder
          style={{
            cursor: 'pointer',
            borderColor: isPersonalPickup === false ? theme.colors.blue[6] : theme.colors.dark[4],
          }}
          onClick={async () => {
            setIsPersonalPickup(false);

            await clearShippingAndPaymentMethodOnCheckoutOrder({
              variables: {
                orderId: order.id,
              },
            });
          }}
        >
          <Group justify="space-between">
            <Group>
              <IconTruckDelivery size={16} />
              <Stack gap={0}>
                <Text>Poslať na adresu</Text>
                <Text fz="xs" c="gray">
                  Kuriérskou službou GLS
                </Text>
              </Stack>
            </Group>
            <ThemeIcon color={isPersonalPickup === false ? 'blue' : 'gray'}>
              {isPersonalPickup === false ? (
                <IconCircleFilled size={16} />
              ) : (
                <IconCircle size={16} />
              )}
            </ThemeIcon>
          </Group>
        </Card>
        <Card
          p="md"
          withBorder
          style={{
            cursor: 'pointer',
            borderColor: isPersonalPickup === true ? theme.colors.blue[6] : theme.colors.dark[4],
          }}
          onClick={async () => {
            setIsPersonalPickup(true);

            await setShippingMethod({
              variables: {
                orderId: order.id,
                shippingMethodId: 'cm4bow5ql0005n82snqsojj6y',
                isPersonal: true,
              },
            });
            await setPaymentMethod({
              variables: {
                orderId: order.id,
                paymentMethodId: 'cm4bow5qp0009n82su9fyiisa',
                isPersonal: true,
              },
            });
          }}
        >
          <Group justify="space-between">
            <Group>
              <IconShoppingBag size={16} />
              <Stack gap={0}>
                <Text>Osobný odber na predajni</Text>
                <Text fz="xs" c="gray">
                  Sama Chalupku 2, dvere č. 3
                </Text>
              </Stack>
            </Group>
            <ThemeIcon color={isPersonalPickup === true ? 'blue' : 'gray'}>
              {isPersonalPickup === true ? (
                <IconCircleFilled size={16} />
              ) : (
                <IconCircle size={16} />
              )}
            </ThemeIcon>
          </Group>
        </Card>
      </Card>

      {isPersonalPickup !== undefined && (
        <>
          {isPersonalPickup ? (
            <Alert color="orange" title="Tel. číslo">
              Zadajte vaše telefónne číslo pre osobný odber. Na toto číslo vám budeme volať.
            </Alert>
          ) : (
            <Alert color="orange" title="Adresa doručenia">
              Zadajte adresu, kam má byť objednávka doručená, a dbajte na správnosť údajov. Táto
              adresa bude uložená pre budúce objednávky. Ak sa pomýlite, kuriér vám nemusí doručiť
              balík!
            </Alert>
          )}

          <AddressSelect order={order} isPersonal={isPersonalPickup} />

          {!isPersonalPickup && (
            <>
              {!order.shippingMethodId && (
                <Alert color="orange" title="Spôsob dopravy">
                  Zvoľte spôsob dopravy. Ak potrebujete pomoc, zavolajte nám.
                </Alert>
              )}

              <OrderCheckoutShippingSelect order={order} />

              {!order.paymentMethodId && (
                <Alert color="orange" title="Spôsob platby">
                  Zvoľte spôsob platby. Ak potrebujete pomoc, zavolajte nám.
                </Alert>
              )}

              <OrderCheckoutPaymentSelect order={order} />
            </>
          )}
          <Button
            color="teal"
            fullWidth
            disabled={
              isPersonalPickup
                ? !order.paymentMethodId || !order.shippingMethodId || !order.address?.phone
                : !order.address ||
                  order.address.name.length === 0 ||
                  order.address.streetLine.length === 0 ||
                  order.address.city.length === 0 ||
                  order.address.postCode.length === 0 ||
                  !order.paymentMethodId ||
                  !order.shippingMethodId
            }
            loading={confirming}
            onClick={async () =>
              await confirmCheckoutOrder({
                variables: { orderId: order.id, isPersonal: false },
              })
            }
          >
            Dokončiť objednávku
          </Button>

          <Alert color="orange" title="Dokončenie objednávky">
            Skontrolujte sumár objednávky a všetky zadané údaje. Po potvrdení objednávky stlačením
            tlačidla bude objednávka úspešne odoslaná. Ďalšie informácie vám pošleme čo najskôr v
            správe.
          </Alert>
        </>
      )}

      <Space h="xl" />
    </Container>
  );
};
